import { router } from "@inertiajs/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatDueLabel, todayIso } from "@/lib/dates";
import LotusIcon from "./LotusIcon";
import Linkified from "./Linkified";
import { useLongPress, type PressPoint } from "@/hooks/useLongPress";
import type { BucketKind } from "@/types";

// A row (or subtree) marked task.leaving fades out and stops accepting clicks/taps.
// Shared with the places that render whole task branches outside TaskRow —
// Lists/Show, MyDay/Show (the renderTaskTree wrapper) and Templates/Show (its own markup).
export const LEAVING_ROW_CLASSES = "pointer-events-none opacity-0 transition-opacity duration-200";

export interface TaskData {
  id: string;
  title: string;
  status: "pending" | "completed";
  position: string;
  completedAt: string | null;
  notes?: string | null;
  listId?: string;
  listName?: string | null;
  parentId?: string | null;
  childrenCount?: number;
  completedChildrenCount?: number;
  nextSubtaskTitle?: string | null;
  createdAt?: string;
  dueOn?: string | null;
  myDayOn?: string | null;
  myDaySequential?: boolean;
  recurrenceRule?: string | null;
  reusable?: boolean;
  visibleFrom?: string | null;
  bucket?: BucketKind;
  leaving?: boolean;
}

interface Props {
  task: TaskData;
  onToggle: (task: TaskData) => void;
  onDelete: (task: TaskData) => void;
  onRequestDelete?: (task: TaskData) => void;
  onSelect?: (task: TaskData, e: React.MouseEvent) => void;
  onToggleExpand?: (task: TaskData) => void;
  onContextMenu?: (point: PressPoint, task: TaskData) => void;
  expanded?: boolean;
  depth?: number;
  draggable?: boolean;
  currentListId?: string;
  hideMyDayBadge?: boolean;
  selected?: boolean;
  dragGroupIds?: string[];
}

const SWIPE_REVEAL_PX = 80;
const SWIPE_COMMIT_PX = 40;
const DIRECTION_LOCK_PX = 6;

export default function TaskRow({
  task,
  onToggle,
  onDelete,
  onRequestDelete,
  onSelect,
  onToggleExpand,
  onContextMenu,
  expanded = false,
  depth = 0,
  draggable = false,
  currentListId,
  hideMyDayBadge = false,
  selected = false,
  dragGroupIds,
}: Props) {
  const isDone = task.status === "completed";
  const isParent = (task.childrenCount ?? 0) > 0;
  const indentPx = depth * 24;

  const sortable = useSortable({
    id: task.id,
    disabled: !draggable,
    data: {
      kind: "task",
      taskId: task.id,
      parentId: task.parentId ?? null,
      isRoot: (task.parentId ?? null) === null,
      currentListId: task.listId,
      groupIds: dragGroupIds,
    },
  });
  const basePadLeft = 20;

  const [isTouch, setIsTouch] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [animating, setAnimating] = useState(false);
  const gestureRef = useRef<{
    startX: number;
    startY: number;
    startOffset: number;
    locked: "horizontal" | "vertical" | null;
  } | null>(null);
  const deleteTapRef = useRef<{ x: number; y: number } | null>(null);
  const deleteFiredRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(pointer: coarse)");
    setIsTouch(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsTouch(e.matches);
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, []);

  const snapBack = useCallback(() => {
    setAnimating(true);
    setSwipeOffset(0);
    setRevealed(false);
  }, []);

  // Close revealed state on outside click, scroll, or Escape.
  useEffect(() => {
    if (!revealed) return;
    function onDown(e: PointerEvent) {
      const target = e.target as Node | null;
      const row = (e.target as Element | null)?.closest?.('[data-testid="task-row"]');
      if (row && row.getAttribute("data-task-id") === task.id) return;
      if (!target) return;
      snapBack();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") snapBack();
    }
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", snapBack, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", snapBack, true);
    };
  }, [revealed, snapBack, task.id]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!isTouch) return;
    if (e.pointerType !== "touch") return;
    gestureRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffset: swipeOffset,
      locked: null,
    };
    setAnimating(false);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const g = gestureRef.current;
    if (!g) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (!g.locked) {
      if (Math.abs(dx) < DIRECTION_LOCK_PX && Math.abs(dy) < DIRECTION_LOCK_PX) return;
      g.locked = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
      if (g.locked === "vertical") {
        gestureRef.current = null;
        return;
      }
    }
    const next = Math.max(-SWIPE_REVEAL_PX, Math.min(0, g.startOffset + dx));
    setSwipeOffset(next);
  };

  const handlePointerUp = () => {
    const g = gestureRef.current;
    if (!g) return;
    gestureRef.current = null;
    if (g.locked !== "horizontal") return;
    setAnimating(true);
    if (swipeOffset <= -SWIPE_COMMIT_PX) {
      setSwipeOffset(-SWIPE_REVEAL_PX);
      setRevealed(true);
    } else {
      setSwipeOffset(0);
      setRevealed(false);
    }
  };

  const handlePointerCancel = () => {
    if (gestureRef.current) gestureRef.current = null;
    snapBack();
  };

  const wrapperStyle = draggable
    ? {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.5 : 1,
      }
    : undefined;

  const longPress = useLongPress(
    isTouch && onContextMenu ? (point) => onContextMenu(point, task) : undefined,
  );

  const draggableProps = draggable
    ? { ref: sortable.setNodeRef, ...sortable.attributes, ...sortable.listeners }
    : {};

  return (
    <div
      {...draggableProps}
      {...longPress}
      onTouchStart={(e) => {
        if (draggable) sortable.listeners?.onTouchStart?.(e);
        longPress.onTouchStart?.(e);
      }}
      style={wrapperStyle}
      onContextMenu={
        onContextMenu || draggable
          ? (e) => {
              // On touch useLongPress opens the menu; the native long-press event would open it twice.
              if (isTouch) {
                e.preventDefault();
                return;
              }
              if (onContextMenu) {
                e.preventDefault();
                onContextMenu(e, task);
              }
            }
          : undefined
      }
      className={`task-row-wrapper${task.leaving ? ` ${LEAVING_ROW_CLASSES}` : ""}`}
      data-testid="task-row"
      data-task-id={task.id}
    >
      <button
        type="button"
        aria-label="Удалить задачу"
        // Delete on pointerup, not just click: after a completed dnd drag Chrome
        // can skip synthesizing click for the next tap, which made parts of the
        // red zone feel dead on touch. Click stays as the keyboard/Enter path,
        // deduped via deleteFiredRef.
        onPointerDown={(e) => {
          deleteFiredRef.current = false;
          deleteTapRef.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerUp={(e) => {
          const start = deleteTapRef.current;
          deleteTapRef.current = null;
          if (!start) return;
          if (Math.abs(e.clientX - start.x) > 10 || Math.abs(e.clientY - start.y) > 10) return;
          deleteFiredRef.current = true;
          // The row unmounts before the browser synthesizes the tap's click, so
          // that click would land on whatever row shifts into this spot — swallow it.
          const stop = (ev: MouseEvent) => {
            ev.stopPropagation();
            ev.preventDefault();
          };
          document.addEventListener("click", stop, { capture: true, once: true });
          setTimeout(() => document.removeEventListener("click", stop, { capture: true }), 400);
          onDelete(task);
          snapBack();
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (deleteFiredRef.current) return;
          deleteFiredRef.current = true;
          onDelete(task);
          snapBack();
        }}
        className="task-row-delete-affordance"
        // z-index lifts the revealed zone above the row so taps anywhere on it land
        // on the button even while the row is still animating over that area.
        style={{
          visibility: swipeOffset === 0 ? "hidden" : "visible",
          zIndex: revealed ? 10 : 0,
        }}
        tabIndex={revealed ? 0 : -1}
        aria-hidden={!revealed}
      >
        <TrashIcon className="w-5 h-5" />
      </button>

      <div
        className={`task-row select-none ${isDone ? "task-row-done" : ""} ${selected ? "task-row-selected" : ""} ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
        style={{
          paddingLeft: basePadLeft + indentPx,
          transform: `translateX(${swipeOffset}px)`,
          transition: animating ? "transform 150ms ease-out" : "none",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <button
          type="button"
          aria-label={isDone ? "Вернуть в активные" : "Завершить задачу"}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(task);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="task-checkbox"
        >
          {isDone && (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>

        <div
          className="task-main"
          onClick={(e) => {
            e.stopPropagation();
            if (revealed) {
              snapBack();
              return;
            }
            onSelect?.(task, e);
          }}
        >
          <div className="task-title-row">
            <span className="task-title">
              <Linkified text={task.title} />
              {task.notes?.trim() && (
                <span className="task-notes-mark" title="Есть заметки" aria-label="Есть заметки">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </span>
              )}
              {!isDone && !expanded && task.nextSubtaskTitle && (
                <span className="task-next-subtask">
                  {" // "}
                  <Linkified text={task.nextSubtaskTitle} />
                </span>
              )}
            </span>
            {isParent && (
              <>
                <button
                  type="button"
                  aria-label={expanded ? "Свернуть подзадачи" : "Развернуть подзадачи"}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand?.(task);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={`task-chevron ${expanded ? "task-chevron-open" : ""}`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Дзен-режим"
                  title="Дзен-режим"
                  onClick={(e) => {
                    e.stopPropagation();
                    router.visit(`/zen?task_id=${task.id}`);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="task-zen"
                >
                  <LotusIcon className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
          <TaskMetaRow task={task} currentListId={currentListId} hideMyDayBadge={hideMyDayBadge} />
        </div>

        {onRequestDelete && !isTouch && (
          <button
            type="button"
            aria-label="Удалить задачу…"
            title="Удалить"
            onClick={(e) => {
              e.stopPropagation();
              onRequestDelete(task);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="task-row-hover-delete"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function TrashIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

interface MetaProps {
  task: TaskData;
  currentListId?: string;
  hideMyDayBadge: boolean;
}

function TaskMetaRow({ task, currentListId, hideMyDayBadge }: MetaProps) {
  const parts: React.ReactNode[] = [];

  if (task.listName && task.listId !== currentListId) {
    parts.push(<span key="list">{task.listName}</span>);
  }

  if (!hideMyDayBadge && task.myDayOn === todayIso()) {
    parts.push(
      <span
        key="myDay"
        title="В «Мой день»"
        aria-label="В «Мой день»"
        className="inline-flex items-center text-gray-400 dark:text-gray-500"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      </span>,
    );
  }

  if ((task.childrenCount ?? 0) > 0) {
    parts.push(
      <span key="subtasks">
        {task.completedChildrenCount ?? 0}/{task.childrenCount}
      </span>,
    );
  }

  if (task.dueOn) {
    const label = formatDueLabel(task.dueOn);
    const cls = label.tone === "overdue" ? "text-red-500" : "";
    parts.push(<span key="due" className={cls}>{label.text}</span>);
  }

  if (task.visibleFrom && task.status === "pending") {
    parts.push(<span key="visible-from">с {task.visibleFrom}</span>);
  }

  if (task.recurrenceRule) {
    parts.push(
      <span
        key="rec"
        title="Повторяется"
        aria-label="Повторяется"
        className="inline-flex items-center text-gray-400 dark:text-gray-500"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </span>,
    );
  }

  if (parts.length === 0) return null;

  return (
    <div className="task-meta">
      {parts.map((p, i) => (
        <span key={i} className="task-meta-part">
          {i > 0 && <span className="task-meta-sep">·</span>}
          {p}
        </span>
      ))}
    </div>
  );
}
